/**
 * Video Timing Utilities
 * Helper functions for converting between time formats and calculating match minutes
 */

/**
 * Convert MM:SS format to seconds (decimal)
 * @param timeString - Time in MM:SS format (e.g., "2:00.5" or "120.5")
 * @returns Time in seconds as number, or null if invalid
 */
export function parseTimeToSeconds(timeString: string): number | null {
  if (!timeString || typeof timeString !== 'string') {
    return null;
  }

  const trimmed = timeString.trim();
  
  // If it's just a number, assume it's already in seconds
  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    const seconds = parseFloat(trimmed);
    return seconds >= 0 ? seconds : null;
  }

  // Try MM:SS format (e.g., "2:00.5" or "120:30")
  const mmssMatch = trimmed.match(/^(\d+):(\d+)(\.\d+)?$/);
  if (mmssMatch) {
    const minutes = parseInt(mmssMatch[1], 10);
    const seconds = parseFloat(mmssMatch[2] + (mmssMatch[3] || ''));
    
    if (minutes >= 0 && seconds >= 0 && seconds < 60) {
      return minutes * 60 + seconds;
    }
  }

  return null;
}

/**
 * Convert seconds to MM:SS format
 * @param seconds - Time in seconds (decimal)
 * @returns Time in MM:SS format (e.g., "2:00.5")
 */
export function formatSecondsToMMSS(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || isNaN(seconds)) {
    return '';
  }

  const totalSeconds = Math.floor(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  const decimal = seconds % 1;

  if (decimal > 0) {
    // Include decimal part
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}${decimal.toFixed(3).substring(1)}`;
  } else {
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
}

/**
 * Timing point interface
 */
export interface TimingPoint {
  match_minute: number;
  video_time_seconds: number;
  event_type: string;
  source_log_id?: string | null;
}

/**
 * Calculate match minute from video time using timing_points array
 * @param videoTimeSeconds - Current video time in seconds
 * @param timingPoints - Array of timing points from match_videos.timing_points
 * @returns Calculated match minute, or null if cannot calculate
 */
export function calculateMatchMinute(
  videoTimeSeconds: number,
  timingPoints: TimingPoint[] | null
): number | null {
  if (!timingPoints || timingPoints.length === 0) {
    return null; // Cannot calculate without timing points
  }

  if (videoTimeSeconds < 0) {
    return null; // Invalid video time
  }

  // Find the most recent timing point before or at current video time
  const relevantPoint = timingPoints
    .filter(point => videoTimeSeconds >= point.video_time_seconds)
    .sort((a, b) => b.video_time_seconds - a.video_time_seconds)[0];

  if (!relevantPoint) {
    // Current video time is before all timing points - use first point
    const firstPoint = timingPoints.sort((a, b) => a.video_time_seconds - b.video_time_seconds)[0];
    if (firstPoint) {
      const elapsedSeconds = firstPoint.video_time_seconds - videoTimeSeconds;
      const elapsedMinutes = Math.floor(elapsedSeconds / 60);
      return Math.max(0, firstPoint.match_minute - elapsedMinutes);
    }
    return null;
  }

  // Calculate elapsed time since this timing point
  const elapsedSeconds = videoTimeSeconds - relevantPoint.video_time_seconds;
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  
  return relevantPoint.match_minute + elapsedMinutes;
}

/**
 * Legacy function for backward compatibility (uses single timing point)
 * @deprecated Use calculateMatchMinute with timing_points array instead
 */
export function calculateMatchMinuteLegacy(
  videoTimeSeconds: number,
  matchStartVideoTimeSeconds: number | null,
  matchStartMatchMinute: number = 0
): number | null {
  if (matchStartVideoTimeSeconds === null || matchStartVideoTimeSeconds === undefined) {
    return null;
  }

  if (videoTimeSeconds < 0) {
    return null;
  }

  const elapsedVideoSeconds = videoTimeSeconds - matchStartVideoTimeSeconds;
  const elapsedMinutes = Math.floor(elapsedVideoSeconds / 60);
  const matchMinute = matchStartMatchMinute + elapsedMinutes;

  return Math.max(0, matchMinute);
}

